// frontend/src/services/searchService.js
import axios from 'axios';
import { getApiBaseURL } from '../utils/api';
import { safeSessionStorage } from '../utils/safeStorage';

// API base URL 가져오기
const BASE_URL = getApiBaseURL();

function getSessionId() {
  let sessionId = safeSessionStorage.getItem('session_id');
  if (!sessionId) {
    sessionId = `session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    safeSessionStorage.setItem('session_id', sessionId);
  }
  return sessionId;
}

/**
 * 검색 관련 서비스를 제공하는 모듈
 */
export const searchService = {
  /**
   * 통합 검색 실행
   * @param {string} query 검색어
   * @param {Object} options 검색 옵션
   * @returns {Promise<Object>} 검색 결과
   */
  async search(query, options = {}) {
    const {
      page = 1,
      limit = 20,
      laws = true,
      kosha_guides = true,
      equipment = true,
      qa_2022 = true,
      qa_construction = true,
      qa_heat = true,
      qa_molit = true,
      qa_nfa = true,
      tags = true,
      board = true,
      pages = true,
      logSearch = true,
    } = options;
    
    if (!query.trim()) {
      return {
        items: [],
        total: 0,
        page,
        limit
      };
    }
    
    try {
      const url = `${BASE_URL}/api/search`;
      const response = await axios.get(url, {
        params: { 
          query,
          page,
          limit,
          session_id: getSessionId(),
          log_search: logSearch ? 'true' : 'false',
          laws: laws ? 'true' : 'false',
          kosha_guides: kosha_guides ? 'true' : 'false',
          equipment: equipment ? 'true' : 'false',
          qa_2022: qa_2022 ? 'true' : 'false',
          qa_construction: qa_construction ? 'true' : 'false',
          qa_heat: qa_heat ? 'true' : 'false',
          qa_molit: qa_molit ? 'true' : 'false',
          qa_nfa: qa_nfa ? 'true' : 'false',
          tags: tags ? 'true' : 'false',
          board: board ? 'true' : 'false',
          pages: pages ? 'true' : 'false'
        }
      });
      
      return response.data;
    } catch (error) {
      console.error('검색 API 오류:', error);
      console.error('API Base URL:', BASE_URL);
      console.error('요청 URL:', `${BASE_URL}/api/search`);
      return {
        items: [],
        total: 0,
        page,
        limit,
        error: error.message
      };
    }
  },
  
  /**
   * 인기 태그 조회
   * @param {number} limit 최대 개수
   * @returns {Promise<Array>} 태그 목록
   */
  async getPopularTags(limit = 20) {
    try {
      const url = `${BASE_URL}/api/tags/popular`;
      const response = await axios.get(url, {
        params: { limit }
      });
      return response.data;
    } catch (error) {
      console.error('인기 태그 조회 오류:', error);
      console.error('API Base URL:', BASE_URL);
      return [];
    }
  },

  /** 최근 실제 통합검색 로그 기준 인기 검색어 조회 */
  async getPopularSearches(limit = 4, days = 30) {
    try {
      const response = await axios.get(`${BASE_URL}/api/search/popular`, {
        params: { limit, days }
      });
      return response.data?.items || [];
    } catch (error) {
      console.error('인기 검색어 조회 오류:', error);
      return [];
    }
  },
  
  /**
   * 텍스트에서 검색어 하이라이트 처리
   * @param {string} text 원본 텍스트
   * @param {string} query 검색어
   * @returns {string} HTML 마크업이 포함된 텍스트
   */
  highlightText(text, query) {
    if (!text || !query) return text;
    
    try {
      // 정규식 특수문자 이스케이핑
      const escapeRegExp = (string) => {
        return string.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
      };
      
      const regex = new RegExp(`(${escapeRegExp(query)})`, 'gi');
      return text.replace(regex, '<mark>$1</mark>');
    } catch (error) {
      console.error('하이라이트 처리 오류:', error);
      return text;
    }
  },
  
  /**
   * 검색 결과 타입에 따른 아이콘 클래스 반환
   * @param {string} type 결과 타입
   * @returns {string} CSS 클래스명
   */
  getResultTypeClass(type) {
    switch (type) {
      case 'law':
        return 'bg-blue-100 text-blue-800';
      case 'kosha_guide':
        return 'bg-teal-100 text-teal-800';
      case 'qa':
        return 'bg-green-100 text-green-800';
      case 'tag':
        return 'bg-purple-100 text-purple-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  }
};

export default searchService;
